<template>
  <!--
    THE NIGHT, CARD BY CARD (Matt, after playtest 12: "instead of it being
    just a big blob of text that we kind of just ignore, I'd like it to be a
    series of cards... that show up and we have to dismiss them... any one
    person tapping next can move it along"). The night is written by the
    engine as sections in one fixed order (NIGHT_SECTIONS in game.js); which
    card is up lives in the game state (`nightRead`), so one tap anywhere
    moves every screen on. The whole night stays in the story so far.
  -->
  <div v-if="card" class="night-cards" :class="{ 'night-cards--big': big }" role="dialog" aria-modal="true" :aria-label="card.title">
    <div class="night-card">
      <span class="night-card__kicker">
        Night · {{ monthName(card.round) }}, year {{ yearOf(card.round) }}
        <span class="night-card__count">{{ at + 1 }} of {{ cards.length }}</span>
      </span>
      <h3 class="night-card__title">{{ card.title }}</h3>
      <ul class="night-card__lines">
        <li v-for="(line, i) in card.lines" :key="i" class="night-card__line" :class="{ 'night-card__line--bad': bad(line) }">{{ line }}</li>
      </ul>
      <button v-if="canTap" class="primary night-card__next" @click="next">
        {{ at + 1 < cards.length ? 'Next' : 'On to the morning' }}
      </button>
      <p v-else class="night-card__wait">Anyone taps Next on their phone</p>
      <span class="night-card__dots" aria-hidden="true">
        <span v-for="(c, i) in cards" :key="c.id" class="night-card__dot" :class="{ 'night-card__dot--on': i <= at }" />
      </span>
    </div>
  </div>
</template>

<script>
import { monthName, yearOf } from '@/engine/calendar';

export default {
  name: 'NightCards',
  props: {
    game: { type: Object, required: true },
    big: { type: Boolean, default: false },
    canTap: { type: Boolean, default: true },
  },
  computed: {
    entry() {
      const log = this.game.log || [];
      for (let i = log.length - 1; i >= 0; i -= 1) if (log[i].kind === 'upkeep') return log[i];
      return null;
    },
    cards() { return this.entry?.night || []; },
    read() { return this.game.nightRead || null; },
    at() { return this.read ? this.read.at : 0; },
    card() {
      if (!this.read || !this.cards.length || this.cards[0].round !== this.read.round) return null;
      return this.cards[this.at] || null;
    },
  },
  methods: {
    monthName,
    yearOf,
    next() { this.$store.dispatch('nightNext', this.read.round); },
    bad(line) { return /famine|not enough|burn|sack|raze|take |starv|lost|got \d|falls|-\d+ happiness|-\d+ population/i.test(line); },
  },
};
</script>

<style scoped>
.night-cards {
  position: fixed; inset: 0; z-index: 60; display: flex; align-items: center; justify-content: center;
  padding: 1rem; background: rgba(6, 10, 14, 0.72); backdrop-filter: blur(4px);
}
.night-card {
  width: min(440px, 100%); display: flex; flex-direction: column; gap: 0.6rem;
  padding: 1.1rem 1.2rem 1rem; border-radius: 16px;
  background: var(--surface, #18221e); border: 1px solid var(--edge, rgba(255, 255, 255, 0.12));
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: night-card-in 0.3s ease both;
}
.night-card__kicker { display: flex; justify-content: space-between; font-size: 0.75rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-dim, #9aa6a0); }
.night-card__count { color: var(--accent, #e0b34a); }
.night-card__title { margin: 0; font-size: 1.35rem; font-weight: 900; color: var(--parchment, #f3e6c8); }
.night-card__lines { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.45rem; }
.night-card__line { font-size: 1rem; line-height: 1.4; color: var(--ink, #e8ecea); padding-left: 10px; border-left: 3px solid var(--edge, rgba(255, 255, 255, 0.15)); }
.night-card__line--bad { border-left-color: var(--danger, #e0442f); color: var(--danger-ink, #ffb4a8); }
.night-card__next { margin-top: 0.3rem; min-height: 46px; font-size: 1.05rem; }
.night-card__wait { margin: 0.3rem 0 0; font-size: 0.85rem; color: var(--ink-dim, #9aa6a0); text-align: center; }
.night-card__dots { display: flex; justify-content: center; gap: 6px; }
.night-card__dot { width: 7px; height: 7px; border-radius: 50%; background: rgba(255, 255, 255, 0.18); }
.night-card__dot--on { background: var(--accent, #e0b34a); }
/* The TV's: read from the sofa. */
.night-cards--big .night-card { width: min(820px, 80vw); gap: 1rem; padding: 2rem 2.4rem 1.6rem; }
.night-cards--big .night-card__kicker { font-size: 1rem; }
.night-cards--big .night-card__title { font-size: clamp(2rem, 3.2vw, 3rem); }
.night-cards--big .night-card__line { font-size: clamp(1.25rem, 1.9vw, 1.7rem); border-left-width: 5px; padding-left: 16px; }
.night-cards--big .night-card__next { min-height: 56px; font-size: 1.3rem; }
@keyframes night-card-in { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: none; } }
</style>
